import { useMemo } from "react"
import type { ChangeEvent, FormEvent } from "react"
import { Link, useSearchParams } from "react-router-dom"
import EntryCompleteDialog from "@/components/entry/EntryCompleteDialog"
import EntryFeeNotice from "@/components/entry/EntryFeeNotice"
import EntryFormSectionHeading from "@/components/entry/EntryFormSectionHeading"
import EntryGymFields from "@/components/entry/EntryGymFields"
import EntryPasswordFields from "@/components/entry/EntryPasswordFields"
import EntryPlayerFields from "@/components/entry/EntryPlayerFields"
import EmptyMessage from "@/components/common/EmptyMessage"
import Loading from "@/components/common/Loading"
import { useEntryForm } from "@/domain/entry/hooks/EntryFormHook"
import { sportLabel } from "@/domain/fight/model/Fight"
import { useTournamentDetail } from "@/domain/tournament/hooks/TournamentDetailHook"
import { fightSportPayloadToJSON } from "@bracket/idl/main/fight/model/enums/FightSportPt"
import type { SelectOption } from "@/domain/entry/model/Entry"

export default function EntryFormPage() {
  // 어느 대회에 신청하는지는 쿼리스트링이 정한다 (대회 목록 · 상세에서 넘어온다)
  const [searchParams] = useSearchParams()
  const tournamentId = searchParams.get("tournamentId") ?? ""

  const { useTournamentDetailState } = useTournamentDetail(tournamentId)
  const {
    detail,
    isLoaded,
    errorMessage: tournamentErrorMessage,
  } = useTournamentDetailState()

  const {
    gym,
    passwordInput,
    players,
    gymErrors,
    passwordErrors,
    playerErrors,
    isAgreed,
    errorMessage,
    isSubmitting,
    registeredEntryNo,
    setGymField,
    setPasswordField,
    setPlayerField,
    setPlayerWeightClass,
    getWeightClassChoices,
    addPlayer,
    removePlayer,
    setAgreed,
    submit,
  } = useEntryForm(tournamentId)

  /** 종목 선택지는 대회가 개최하는 종목만 노출한다 */
  const sportOptions = useMemo<SelectOption[]>(
    () =>
      (detail?.sports ?? []).map((sport) => ({
        value: fightSportPayloadToJSON(sport),
        label: sportLabel(sport),
      })),
    [detail],
  )

  const sportSummary = useMemo(
    () =>
      sportOptions.map((option) => ({
        label: option.label,
        count: players.filter((player) => player.sport === option.value).length,
      })),
    [sportOptions, players],
  )

  function onSubmitEntry(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit()
  }

  function onClickAddPlayer() {
    addPlayer()
  }

  function onChangeAgreed(event: ChangeEvent<HTMLInputElement>) {
    setAgreed(event.target.checked)
  }

  // 대회를 고르지 않고 들어온 경우 — 목록으로 안내한다
  if (!tournamentId) {
    return (
      <section className="app-page max-w-[896px]">
        <EmptyMessage message="먼저 신청할 대회를 선택해 주세요." />
        <Link
          to="/tournaments"
          className="focus-ring app-btn app-btn-primary mx-auto flex max-w-xs"
        >
          대회 목록으로
        </Link>
      </section>
    )
  }

  return (
    <section className="app-page">
      <div className="app-card-ink">
        <p className="app-txt-eyebrow text-app-primary-400">
          TOURNAMENT APPLICATION
        </p>

        {!isLoaded && <Loading label="대회 정보를 불러오는 중" />}

        {isLoaded && tournamentErrorMessage && (
          <p
            role="alert"
            className="app-txt-body2 mt-3 text-app-primary-300"
          >
            {tournamentErrorMessage}
          </p>
        )}

        {isLoaded && detail && (
          <>
            <h1 className="app-txt-h1 mt-3">{detail.name}</h1>
            <dl className="mt-7 grid gap-4 border-t border-white/10 pt-6 sm:grid-cols-3">
              <div>
                <dt className="app-txt-caption text-app-secondary-700">
                  대회일
                </dt>
                <dd className="app-txt-body2 mt-1">{detail.periodText}</dd>
              </div>
              <div>
                <dt className="app-txt-caption text-app-secondary-700">장소</dt>
                <dd className="app-txt-body2 mt-1">{detail.venue}</dd>
              </div>
              <div>
                <dt className="app-txt-caption text-app-secondary-700">
                  접수 마감일
                </dt>
                <dd className="app-txt-body2 mt-1 font-bold text-app-primary-400">
                  {detail.entryCloseText}
                </dd>
              </div>
            </dl>
          </>
        )}
      </div>

      <div className="app-alert app-alert-brand mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p>체육관 신청 내역을 확인하시나요?</p>
        <Link
          to="/application/lookup"
          className="focus-ring app-txt-button text-app-primary-600 hover:underline"
        >
          신청 조회하기 →
        </Link>
      </div>

      <form
        onSubmit={onSubmitEntry}
        noValidate
        className="mt-5 flex flex-col gap-5"
      >
        <EntryGymFields
          gym={gym}
          errors={gymErrors}
          onChange={setGymField}
        >
          <EntryPasswordFields
            passwordInput={passwordInput}
            errors={passwordErrors}
            onChange={setPasswordField}
          />
        </EntryGymFields>

        <section className="app-card">
          <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
            <EntryFormSectionHeading
              step="02"
              title="선수 신청"
              description="생년월일 · 성별 · 종목을 넣으면 고를 수 있는 체급이 나옵니다."
            />
            <button
              type="button"
              onClick={onClickAddPlayer}
              className="focus-ring app-btn app-btn-brand shrink-0"
            >
              + 선수 추가
            </button>
          </div>

          <div className="mt-6 flex flex-col gap-4">
            {players.map((player, index) => (
              <EntryPlayerFields
                key={player.key}
                player={player}
                index={index}
                errors={playerErrors[player.key] ?? {}}
                sportOptions={sportOptions}
                weightClassChoices={getWeightClassChoices(player.key)}
                canDelete={players.length > 1}
                onChange={(field, value) =>
                  setPlayerField(player.key, field, value)
                }
                onChangeWeightClass={(optionKey) =>
                  setPlayerWeightClass(player.key, optionKey)
                }
                onDelete={() => removePlayer(player.key)}
              />
            ))}
          </div>
        </section>

        <section className="app-card">
          <EntryFormSectionHeading
            step="03"
            title="신청 요약 및 동의"
            description="입력한 선수와 종목 수를 확인해 주세요."
          />

          <div className="app-inset mt-6">
            <p className="app-txt-h3">
              총 신청 선수{" "}
              <span className="text-app-primary-600">{players.length}명</span>
            </p>
            <ul className="mt-3 flex list-none flex-wrap gap-2">
              {sportSummary.map((summary) => (
                <li
                  key={summary.label}
                  className="app-chip"
                >
                  {summary.label} {summary.count}명
                </li>
              ))}
            </ul>
          </div>

          {/* 참가비 · 입금 계좌 — 동의사항처럼 접수 화면 안에서 읽고 넘어가는 자리 */}
          <div className="mt-5 border border-app-secondary-500">
            <p className="app-txt-caption border-b border-app-secondary-500 bg-app-secondary-300 px-4 py-2.5 text-app-text-200">
              참가비 · 입금 계좌
            </p>
            <div className="max-h-72 overflow-y-auto p-4">
              <EntryFeeNotice />
            </div>
          </div>

          <div className="mt-5 flex items-start gap-3 border border-app-secondary-500 p-4">
            <input
              id="entry-agreement"
              type="checkbox"
              checked={isAgreed}
              onChange={onChangeAgreed}
              className="focus-ring mt-1 size-4 accent-app-primary-500"
            />
            <label
              htmlFor="entry-agreement"
              className="app-txt-body2 leading-6 text-app-text-300"
            >
              본인은 위 신청 내용을 사실대로 작성하였으며, 허위 사실 기재 시
              대회 참가 제한 및 기타 불이익을 받을 수 있음에 동의합니다.
            </label>
          </div>
        </section>

        {errorMessage && (
          <p
            role="alert"
            className="app-alert app-alert-error"
          >
            {errorMessage}
          </p>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="focus-ring app-btn app-btn-lg app-btn-primary app-btn-block"
        >
          참가 신청 제출
        </button>
      </form>

      {registeredEntryNo && <EntryCompleteDialog entryNo={registeredEntryNo} />}
    </section>
  )
}
